import { useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import CustomerPortalLayout from "components/storefront/CustomerPortalLayout";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useAuth } from "context/AuthContext";
import { get, post } from "lib/api";
import { isCustomerRole } from "lib/portalRoles";
import { useQuery } from "@tanstack/react-query";
import useStorefrontCopy from "hooks/useStorefrontCopy";

export default function CustomerValveBodyWarranty() {
  const { token, pluginRole, authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { documentId } = useParams();
  const { t } = useStorefrontCopy();
  const role = String(pluginRole || "").toLowerCase();
  const isCustomer = isCustomerRole(role);
  const [form, setForm] = useState({
    vehicleYear: "",
    vehicleMake: "",
    vehicleModel: "",
    vehicleTrim: "",
    vehicleVin: "",
    issueCodes: "",
    issueDescription: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const meQuery = useQuery({
    queryKey: ["storefront-me", "capabilities", "warranty"],
    queryFn: () => get("/api/storefront/me"),
    enabled: !!token && isCustomer,
    staleTime: 15000,
  });
  const detailQuery = useQuery({
    queryKey: ["storefront-valve-body", documentId, "warranty-create"],
    queryFn: () => get(`/api/storefront/valve-bodies/${documentId}`),
    enabled: !!token && isCustomer && !!documentId,
    staleTime: 15000,
  });
  const canSubmitWarranty =
    meQuery.data?.capabilities?.submitWarranty !== false;
  const activeWarranty =
    detailQuery.data?.data?.lifecycle?.activeWarranty || null;
  const activeWarrantyStatus = String(activeWarranty?.status || "")
    .trim()
    .toLowerCase();
  const hasActiveWarranty =
    !!activeWarranty &&
    activeWarrantyStatus &&
    !["closed", "denied"].includes(activeWarrantyStatus);
  const canCreateWarranty = canSubmitWarranty && !hasActiveWarranty;
  const activeWarrantyReference =
    activeWarranty?.rmaNumber || activeWarranty?.documentId || null;
  const activeWarrantyDocumentId = String(
    activeWarranty?.documentId || ""
  ).trim();

  if (!token)
    return (
      <Navigate
        to="/authentication/sign-in"
        state={{ from: location }}
        replace
      />
    );
  if (authLoading || pluginRole == null) return null;
  if (!isCustomer) return <Navigate to="/dashboard" replace />;
  if (!documentId) return <Navigate to="/account/valve-bodies" replace />;

  const onSubmit = async () => {
    if (!canCreateWarranty) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await post(
        `/api/storefront/valve-bodies/${documentId}/warranty-claims`,
        {
          data: {
            vehicleYear: Number(form.vehicleYear),
            vehicleMake: form.vehicleMake,
            vehicleModel: form.vehicleModel,
            vehicleTrim: form.vehicleTrim,
            vehicleVin: form.vehicleVin,
            issueCodes: form.issueCodes
              .split(",")
              .map((value) => value.trim())
              .filter(Boolean),
            issueDescription: form.issueDescription,
            notes: form.notes,
          },
        }
      );
      const claim = res?.data?.claim;
      const warning = claim?.supportRequired
        ? ` ${claim?.supportMessage || ""}`
        : "";
      setMessage({
        type: claim?.supportRequired ? "warning" : "success",
        text: `Warranty claim created${
          claim?.rmaNumber ? ` (${claim.rmaNumber})` : ""
        }.${warning}`,
      });
      if (claim?.documentId) {
        navigate(`/account/warranty-claims/${claim.documentId}`);
        return;
      }
    } catch (e) {
      setMessage({
        type: "error",
        text:
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to create warranty claim.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <CustomerPortalLayout
      title="Warranty Claim"
      subtitle="Submit warranty details, generate an RMA, then request your preferred return shipping option."
      actions={
        <MDButton
          variant="outlined"
          color="info"
          onClick={() => navigate(`/account/valve-bodies/${documentId}`)}
        >
          Back
        </MDButton>
      }
    >
      {message ? (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      ) : null}
      {!canSubmitWarranty ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your membership does not allow creating warranty claims.
        </Alert>
      ) : null}
      {hasActiveWarranty ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("warranty.activeClaim", "An active claim is already open")}
          {activeWarrantyReference ? ` (${activeWarrantyReference})` : ""}.
          {` ${t(
            "warranty.oneActivePerItem",
            "Only one active warranty claim is allowed per item at a time."
          )}`}
          {activeWarrantyDocumentId ? (
            <Box sx={{ mt: 1 }}>
              <MDButton
                variant="text"
                color="info"
                size="small"
                sx={{ px: 0 }}
                onClick={() =>
                  navigate(
                    `/account/warranty-claims/${activeWarrantyDocumentId}`
                  )
                }
              >
                Open active claim details
              </MDButton>
            </Box>
          ) : null}
        </Alert>
      ) : null}
      <MDTypography variant="body2" color="text" sx={{ opacity: 0.8, mb: 2 }}>
        Vehicle information is required so repeated RMA rules can be enforced
        correctly.
      </MDTypography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <MDInput
            label="Vehicle year"
            fullWidth
            value={form.vehicleYear}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, vehicleYear: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MDInput
            label="Make"
            fullWidth
            value={form.vehicleMake}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, vehicleMake: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MDInput
            label="Model"
            fullWidth
            value={form.vehicleModel}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, vehicleModel: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDInput
            label="Trim"
            fullWidth
            value={form.vehicleTrim}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, vehicleTrim: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDInput
            label="VIN"
            fullWidth
            value={form.vehicleVin}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, vehicleVin: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="Codes (comma separated)"
            fullWidth
            value={form.issueCodes}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, issueCodes: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="Problem description"
            fullWidth
            multiline
            minRows={4}
            value={form.issueDescription}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, issueDescription: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="Additional notes"
            fullWidth
            multiline
            minRows={3}
            value={form.notes}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, notes: e.target.value }))
            }
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
        <MDButton
          variant="gradient"
          color="info"
          disabled={saving || !canCreateWarranty}
          onClick={onSubmit}
        >
          {saving ? "Submitting..." : "Create warranty claim"}
        </MDButton>
      </Box>
    </CustomerPortalLayout>
  );
}
